import { NextPage } from 'next';
import { getCsrfToken, signIn } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useId } from 'react';

import {
  useAlertAutoFocus,
  useCallbackUrl,
  useNextRegisterPush,
} from '@/common/hooks';
import {
  BaseInput,
  OneTimeAccessForm,
  OneTimeAccessPinForm,
  useOneTimeAccessForm,
} from '@/sleeper/components';
import { useIsTelesales } from '@/sleeper/hooks';

import logoNewWhite from '../assets/img/logo-new-white.svg';

interface Props {
  csrfToken: string;
}

export const TIME_LEFT_DEFAULT = 59 * 60 + 58;

export const LoginPage: NextPage<Props> = ({ csrfToken }) => {
  const { query, basePath } = useRouter();
  const emailId = useId();
  const passwordId = useId();
  const callbackUrl = useCallbackUrl();
  const register = useNextRegisterPush(callbackUrl);
  const alertRef = useAlertAutoFocus();
  const isTelesales = useIsTelesales();
  const {
    getOneTimeAccess,
    reset: resetOneTimeAccess,
    data: oneTimeAccessData,
    error: oneTimeAccessError,
    isLoading: isOneTimeAccessLoading,
    isSuccess: isOneTimeAccessSuccess,
  } = useOneTimeAccessForm();
  const handleLoginSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const defaultCallback = encodeURIComponent(
        basePath + '/my-account/dashboard',
      );
      let callback = callbackUrl.endsWith('login')
        ? defaultCallback
        : callbackUrl;

      try {
        if (callback && decodeURIComponent(callback)?.startsWith('http')) {
          const url = new URL(callback);

          if (url.origin !== window.location.origin) {
            callback = defaultCallback;
          } else {
            callback = encodeURIComponent(url.pathname + url.search + url.hash);
          }
        }
      } catch (error) {
        callback = defaultCallback;
      }

      signIn('credentials', {
        email: e.currentTarget.email.value,
        password: e.currentTarget.password.value,
        callbackUrl: decodeURIComponent(callback),
      });
    },
    [basePath, callbackUrl],
  );

  if (isOneTimeAccessSuccess) {
    return (
      <OneTimeAccessPinForm
        csrfToken={csrfToken}
        onCancel={resetOneTimeAccess}
        isTelesales={isTelesales}
        timeLeftDefault={TIME_LEFT_DEFAULT}
        validUntil={oneTimeAccessData?.data.validUntil}
        nonce={oneTimeAccessData?.data.otpNonce}
        orderId={oneTimeAccessData?.data.orderId}
      />
    );
  }

  return (
    <div className="container-without-login">
      <div className="row-without-login ">
        <div className="forms-container">
          <div className="box box--without-login">
            <div className="logo-container">
              {isTelesales ? (
                <div className="logo logo--without-login">
                  <span className="sr-only">Caledonian Sleeper</span>
                </div>
              ) : (
                <a
                  href="https://www.sleeper.scot"
                  className="logo logo--without-login"
                >
                  <span className="sr-only">Caledonian Sleeper</span>
                </a>
              )}
            </div>
            {query.error && (
              <div
                className="msg msg--warning msg--sm msg--ico mb-2"
                role="alert"
                ref={alertRef}
                tabIndex={0}
              >
                {query.error}
              </div>
            )}

            <form onSubmit={handleLoginSubmit}>
              <input name="csrfToken" type="hidden" defaultValue={csrfToken} />
              <h1 className="heading__title heading__title--login mb-2">
                Sign in to your account
              </h1>
              <div className="mb-2">
                For guests who already have an account.
              </div>

              <BaseInput
                name="email"
                type="email"
                id={emailId}
                label="Email address"
                customClass="textfield--login"
                placeholder="" // leave placeholder empty - hack to fix autofill bug on chrome
              />
              <BaseInput
                name="password"
                type="password"
                id={passwordId}
                label="Password"
                customClass="textfield--login"
                placeholder="" // leave placeholder empty - hack to fix autofill bug on chrome
              />

              <div className="mb-2">
                <button
                  type="submit"
                  className="btn btn--secondary btn--medium no-uppercase mb-1"
                >
                  Sign in
                </button>
              </div>

              <div className="buttons-container--with-border pb-4">
                <Link href="/password-recovery" className="db link">
                  Forgotten your password?
                </Link>

                <button
                  type="button"
                  onClick={() => register()}
                  className="link pb-0"
                >
                  Create an account
                </button>
              </div>
            </form>

            <OneTimeAccessForm
              onSubmit={getOneTimeAccess}
              csrfToken={csrfToken}
              error={oneTimeAccessError?.message || null}
              alertRef={alertRef}
              isLoading={isOneTimeAccessLoading}
            />
          </div>
        </div>
        <div className="image-container">
          {isTelesales ? (
            <>
              <Image
                src={logoNewWhite}
                alt="Caledonian Sleeper"
                width={200}
                height={70}
              />
            </>
          ) : (
            <>
              <a href="https://www.sleeper.scot">
                <Image
                  src={logoNewWhite}
                  alt="Caledonian Sleeper"
                  width={200}
                  height={70}
                />
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export async function getServerSideProps(context) {
  const csrfToken = await getCsrfToken(context);
  return {
    props: { csrfToken },
  };
}

export default LoginPage;
